import { ChargeTypes } from '@enums/charge-types';
import { PaymentTypes } from '@enums/payment-types';
import { CreditCardPayment, ExternalPayment, GiftCardPayment, Payment } from '@medology/ngx-payment';
import { Upsell } from '@models/upsell';

/**
 * Represents a request object used to store an upsell on an order.
 */
export class LegacyUpsellStoreRequest {
  public upsell: Upsell | Upsell[];
  public charge_type?: ChargeTypes;
  public card_number?: string;
  public card_exp?: Date;
  public billing_zip_code?: string;
  public card_holder?: string;
  public cvv?: string;
  public charge_amount?: number;
  public nonce?: string;
  public gift_card_code?: string;

  public constructor(upsell: Upsell | Upsell[], paymentType?: PaymentTypes) {
    this.upsell = upsell;

    if (paymentType) {
      this.charge_type = this.getChargeType(paymentType);
    }
  }

  /**
   * Return the legacy upsell store request with the payment parsed from ngx-payment payment classes
   *
   * @param {Upsell} upsell the upsell to be added
   * @param {Payment[]} payments the payments used from ngx-payment
   * @param {string} cardHolder the cardholder name used when payment is credit card
   *
   * @returns {LegacyUpsellStoreRequest} the legacy upsell store request
   */
  public static fromPayments(upsell: Upsell, payments: Payment[], cardHolder: string): LegacyUpsellStoreRequest {
    const legacyUpsellRequest = new LegacyUpsellStoreRequest(upsell);
    const [firstPayment, giftCardAsSecondaryPayment] = payments;

    legacyUpsellRequest.parsePaymentInfoFromPayment(firstPayment, cardHolder);
    if (giftCardAsSecondaryPayment) {
      legacyUpsellRequest.gift_card_code = (giftCardAsSecondaryPayment as GiftCardPayment).code;
    }

    return legacyUpsellRequest;
  }

  /**
   * Return the legacy upsell store request with the default payment based on payment type used previously
   *
   * @param {Upsell} upsell the upsell to be added
   * @param {string} payment the payment previously used
   *
   * @returns {LegacyUpsellStoreRequest} the legacy upsell store request
   */
  public static useDefaultPayment(upsell: Upsell, payment: string): LegacyUpsellStoreRequest {
    const defaultChargeType = payment === PaymentTypes.PayLater ? PaymentTypes.PayLater : PaymentTypes.Recharge;

    return new LegacyUpsellStoreRequest(upsell, defaultChargeType);
  }

  /**
   * Parse legacy payment info for legacy upsell endpoint from the ngx-payment payment class
   *
   * @param {Payment} payment the Payment class from ngx-payment package
   * @param {string} cardholder the cardholder name used when payment is credit card
   */
  public parsePaymentInfoFromPayment(payment: Payment, cardholder: string): void {
    if (payment instanceof ExternalPayment) {
      this.nonce = payment.nonce;

      return;
    }

    if (payment instanceof CreditCardPayment) {
      this.charge_type = ChargeTypes['Credit Card'];
      this.card_number = payment.cardNumber;
      this.card_exp = new Date(payment.expirationYear, +payment.expirationMonth - 1);
      this.billing_zip_code = payment.billingZipCode;
      this.cvv = payment.cardCvv;
      this.card_holder = cardholder;

      return;
    }

    if (payment instanceof GiftCardPayment) {
      this.charge_type = ChargeTypes['Gift Card'];
      this.gift_card_code = payment.code;

      return;
    }
  }

  /**
   * Returns the charge type based on the payment type
   *
   * @param {PaymentTypes} paymentType The payment type
   */
  private getChargeType(paymentType: PaymentTypes): ChargeTypes {
    const chargeType = ChargeTypes[paymentType];
    if (!chargeType) {
      throw new Error(`Charge type not allowed for payment type: ${paymentType}`);
    }

    return chargeType;
  }
}
